import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from './SEOHead';

interface AboutUsProps {
  onClose?: () => void;
}

const AboutUs: React.FC<AboutUsProps> = ({ onClose }) => {
  const features = [
    {
      icon: 'fas fa-globe',
      title: '网站管理',
      subtitle: 'Web Management',
      description: '轻松管理所有提示词，就像管理手机相册一样简单。支持分类整理、快速搜索，再也不用担心找不到好用的提示词',
      highlights: ['智能分类', '秒速搜索', '标签管理', '历史记录'],
      color: 'from-gray-600 to-gray-800',
      bgPattern: 'bg-gradient-to-br from-gray-50/80 via-white to-gray-50/50 dark:from-gray-800/80 dark:via-gray-700 dark:to-gray-800/50',
          accentColor: 'text-gray-700 dark:text-gray-300',
    borderColor: 'border-gray-200 dark:border-gray-700'
    },
    {
      icon: 'fas fa-puzzle-piece',
      title: '浏览器插件',
      subtitle: 'Browser Extension',
      description: '在任何网站一键调用你的提示词。无论是ChatGPT、Claude还是其他AI工具，都能无缝使用你精心收藏的提示词',
      highlights: ['一键调用', '全平台兼容', '实时同步', '快捷键支持'],
      color: 'from-primary to-primary-dark',
      bgPattern: 'bg-gradient-to-br from-red-50/80 via-white to-red-50/50 dark:from-red-900/20 dark:via-gray-700 dark:to-red-900/20',
      accentColor: 'text-primary dark:text-red-400',
      borderColor: 'border-red-200 dark:border-red-700'
    },
    {
      icon: 'fas fa-robot',
      title: 'MCP协议',
      subtitle: 'MCP Protocol',
      description: '让编程工具直接访问你的提示词库。在Cursor、Cline等代码编辑器中，AI助手能直接使用你的专业提示词',
      highlights: ['编程工具集成', 'AI助手增强', '开发效率提升', '专业场景优化'],
      color: 'from-green-600 to-green-800',
      bgPattern: 'bg-gradient-to-br from-green-50/80 via-white to-green-50/50 dark:from-green-900/20 dark:via-gray-700 dark:to-green-900/20',
      accentColor: 'text-green-700 dark:text-green-400',
      borderColor: 'border-green-200 dark:border-green-700'
    }
  ];

  // 结构化数据
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "AI咖",
    "alternateName": "AI Prompter",
    "description": "让AI更好用的提示词管家，提供网站管理、浏览器插件、MCP协议三位一体的AI提示词解决方案",
    "url": "https://www.aiprompter.cc",
    "logo": "https://www.aiprompter.cc/images/icons/icon128.png",
    "foundingDate": "2024",
    "founders": [
      {
        "@type": "Person",
        "name": "AI咖团队"
      }
    ],
    "sameAs": [
      "https://www.aiprompter.cc"
    ],
    "knowsAbout": [
      "AI提示词管理",
      "人工智能",
      "浏览器插件",
      "MCP协议",
      "AI工具集成"
    ],
    "offers": {
      "@type": "Service",
      "name": "AI提示词管理服务",
      "description": "提供完整的AI提示词管理解决方案，包括网站管理、浏览器插件和MCP协议集成",
      "serviceType": "AI工具",
      "areaServed": "全球",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "AI咖服务",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "网站管理",
              "description": "轻松管理所有提示词，支持分类整理、快速搜索"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "浏览器插件",
              "description": "在任何网站一键调用提示词，支持ChatGPT、Claude等AI工具"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "MCP协议",
              "description": "让编程工具直接访问提示词库，支持Cursor、Cline等开发工具"
            }
          }
        ]
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* SEO优化 */}
      <SEOHead 
        title="关于我们 - AI咖：让AI更好用的提示词管家"
        description="AI咖是专业的AI提示词管理平台，提供网站管理、浏览器插件、MCP协议三位一体的解决方案。让每个人都能轻松驾驭AI的力量，一次保存，随处使用。"
        keywords="AI咖, AI提示词管理, 人工智能, 浏览器插件, MCP协议, ChatGPT, Claude, AI工具, 提示词收藏, 智能管理"
        ogImage="https://www.aiprompter.cc/images/icons/icon128.png"
        canonical="https://www.aiprompter.cc/about"
      />
      
      {/* 结构化数据 */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-red-900"></div>
        <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-20 left-20 w-64 h-64 bg-white dark:bg-gray-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative container mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            {/* Logo和品牌 */}
            <div className="flex items-center justify-center mb-8">
              <div className="relative">
                <img 
                  src="/images/icons/icon128.png" 
                  alt="AI咖 Logo - AI提示词管理平台" 
                  className="w-20 h-20 rounded-2xl shadow-2xl border-4 border-white/20" 
                />
                <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl blur opacity-50"></div>
              </div>
              <div className="ml-6">
                              <h1 className="text-6xl font-bold bg-gradient-to-r from-white to-red-100 bg-clip-text text-transparent">
                AI咖 - 专业的AI提示词管理平台
              </h1>
                <p className="text-red-100 text-lg font-medium tracking-wide">让AI更好用的提示词管家</p>
              </div>
            </div>

            {/* 主标语 */}
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              让每个人都能
              <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent mx-3">
                轻松驾驭AI
              </span>
              的力量
            </h2>
            
            <p className="text-xl text-red-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              收集、管理、使用提示词就像整理书签一样简单，让AI工具为你所用
            </p>

            {/* 特色标签 */}
            <div className="flex flex-wrap justify-center gap-3">
              {['简单易用', '随时调用', '效果翻倍', '完全免费'].map((tag, index) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="px-4 py-2 bg-white/10 dark:bg-gray-800/20 backdrop-blur-sm border border-white/20 dark:border-gray-600/30 rounded-full text-sm font-medium hover:bg-white/20 dark:hover:bg-gray-700/30 transition-all duration-300"
                >
                  {tag}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-6">
        {/* 使命愿景 - 重新设计 */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="py-24"
        >
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
                为什么选择AI咖
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                让AI工具
                <br />
                <span className="bg-gradient-to-r from-primary to-red-600 bg-clip-text text-transparent">
                  变得更好用
                </span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>
                  你是否遇到过这些烦恼：<strong className="text-primary font-semibold">好用的提示词总是找不到？</strong>
                  每次都要重新输入长长的提示词？在不同AI工具间切换很麻烦？
                </p>
                <p>
                  AI咖就是为了解决这些问题而生的。<strong className="text-gray-900 dark:text-white font-semibold">我们让提示词管理变得像收藏网页一样简单</strong>，
                  让你能在任何地方、任何AI工具中快速使用你的"秘密武器"。
                </p>
                <p>
                  <strong className="text-primary font-semibold">一次保存，随处使用</strong> — 这就是AI咖的核心理念。
                </p>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 border border-gray-200 dark:border-gray-600 shadow-lg">
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      { icon: 'fas fa-save', label: '一键保存', desc: '发现好提示词立即收藏', color: 'from-primary to-red-600' },
                      { icon: 'fas fa-search', label: '快速查找', desc: '秒找你需要的提示词', color: 'from-gray-600 to-gray-800' },
                      { icon: 'fas fa-mouse-pointer', label: '一键使用', desc: '浏览器里直接调用', color: 'from-green-600 to-green-800' },
                      { icon: 'fas fa-sync', label: '自动同步', desc: '所有设备实时同步', color: 'from-gray-700 to-gray-900' }
                    ].map((item, index) => (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 * index }}
                        className="text-center"
                      >
                        <div className={`w-12 h-12 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                          <i className={`${item.icon} text-white text-lg`} aria-hidden="true"></i>
                        </div>
                        <h4 className="font-bold text-gray-900 dark:text-white mb-1">{item.label}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 核心功能 - 现代化设计 */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="py-24"
        >
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
              三大神器
            </span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              让AI工具
              <span className="bg-gradient-to-r from-primary to-red-600 bg-clip-text text-transparent">
                听你指挥
              </span>
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              三个工具配合使用，让你的AI使用体验从此不同
            </p>
          </div>

          <div className="space-y-12">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -60 : 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.2 * index }}
                className={`relative ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} 
                          flex flex-col lg:flex items-center gap-12 lg:gap-16`}
              >
                {/* 功能图标和标题 */}
                <div className="flex-1 lg:max-w-md">
                  <div className="relative">
                    {/* 背景装饰 */}
                    <div className={`absolute -inset-4 bg-gradient-to-r ${feature.color} rounded-3xl blur-2xl opacity-20`}></div>
                    
                    <div className={`relative ${feature.bgPattern} rounded-2xl p-8 border ${feature.borderColor} backdrop-blur-sm shadow-lg`}>
                      <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                        <i className={`${feature.icon} text-2xl text-white`}></i>
                      </div>
                      
                      <div className="mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                        <p className={`text-sm font-medium ${feature.accentColor} tracking-wide uppercase`}>
                          {feature.subtitle}
                        </p>
                      </div>

                                              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
                        {feature.description}
                      </p>

                      {/* 特性标签 */}
                      <div className="flex flex-wrap gap-2">
                        {feature.highlights.map((highlight, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 bg-white/80 dark:bg-gray-700/80 ${feature.accentColor} rounded-full text-sm font-medium border border-gray-200/50 dark:border-gray-600/50 shadow-sm`}
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 功能演示区域 */}
                <div className="flex-1 lg:max-w-lg">
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
                    <div className="space-y-4">
                      {/* 模拟界面 */}
                      <div className="flex items-center space-x-3 mb-6">
                        <div className={`w-3 h-3 bg-gradient-to-r ${feature.color} rounded-full`}></div>
                        <div className={`flex-1 h-2 bg-gradient-to-r ${feature.color} rounded-full opacity-20`}></div>
                      </div>
                      
                      {Array.from({ length: 3 }).map((_, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.6, delay: 0.1 * idx }}
                          className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600"
                        >
                          <div className={`w-8 h-8 bg-gradient-to-r ${feature.color} rounded-lg opacity-80 shadow-sm`}></div>
                          <div className="flex-1">
                            <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded mb-2" style={{ width: `${80 - idx * 10}%` }}></div>
                            <div className="h-2 bg-gray-100 dark:bg-gray-500 rounded" style={{ width: `${60 - idx * 5}%` }}></div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* 工作流程可视化 - 现代化重设计 */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="py-24"
        >
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide uppercase mb-4">
              使用流程
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              <span className="bg-gradient-to-r from-primary to-red-600 bg-clip-text text-transparent">
                简单三步
              </span>
              开启AI高效之旅
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              从收集到使用，让提示词发挥最大价值
            </p>
          </div>

          <div className="relative max-w-6xl mx-auto">
            {/* 中央连接动画 */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-1 bg-gradient-to-r from-gray-300 via-primary/40 via-green-300 to-gray-300 rounded-full opacity-50 hidden lg:block"></div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 lg:gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 * index }}
                  className="relative group"
                >
                  <div className="text-center">
                    {/* 主图标 */}
                    <div className="relative mx-auto mb-8">
                      <div className={`absolute -inset-4 bg-gradient-to-r ${feature.color} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500`}></div>
                      <div className={`relative w-32 h-32 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center shadow-2xl group-hover:scale-105 transition-transform duration-500`}>
                        <i className={`${feature.icon} text-4xl text-white`}></i>
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                        {index + 1}
                      </div>
                    </div>

                    {/* 标题和描述 */}
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                      {index === 0 && '在网站上整理和分类你的提示词库'}
                      {index === 1 && '在浏览器中一键调用你的提示词'}
                      {index === 2 && '在编程工具中直接使用专业提示词'}
                    </p>

                    {/* 详细信息卡片 */}
                    <div className={`${feature.bgPattern} rounded-xl p-6 border ${feature.borderColor} group-hover:shadow-lg transition-shadow duration-300`}>
                      <h4 className={`font-semibold ${feature.accentColor} mb-3`}>主要特点</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {feature.highlights.map((highlight, idx) => (
                          <div
                            key={idx}
                            className="flex items-center text-sm text-gray-700 dark:text-gray-300"
                          >
                            <i className="fas fa-check text-green-500 mr-2 text-xs"></i>
                            {highlight}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 连接线（仅在大屏幕显示） */}
                  {index < features.length - 1 && (
                    <div className="hidden lg:block absolute top-16 -right-8 z-10">
                      <div className="w-16 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 relative">
                        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-gray-400 rounded-full"></div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* 用户价值主张 */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="py-24"
        >
          <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-12 border border-gray-200 dark:border-gray-700 overflow-hidden shadow-lg">
            {/* 背景装饰 */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-red-100 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-gray-100 to-gray-200 rounded-full translate-y-24 -translate-x-24"></div>
            
            <div className="relative text-center max-w-4xl mx-auto">
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                开始使用
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
                加入AI咖社区
                <span className="bg-gradient-to-r from-primary to-red-600 bg-clip-text text-transparent">
                  让AI为你所用
                </span>
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                无论你是AI新手还是资深玩家，AI咖都能让你的AI使用体验更上一层楼。
                从此告别重复输入，告别遗忘困扰，让每次与AI的对话都更加高效精准。
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center">
                  <i className="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>完全免费使用</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>无限提示词存储</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>多设备同步</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check-circle text-green-500 mr-2"></i>
                  <span>持续功能更新</span>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* 联系我们 */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="py-16 text-center"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">联系我们</h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            遇到问题或有好建议？我们很乐意听到你的声音
          </p>
        </motion.section>
      </div>

      {/* 返回按钮 */}
      {onClose && (
        <div className="fixed top-6 right-6 z-50">
          <button
            onClick={onClose}
            className="w-12 h-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center border border-gray-200/50 dark:border-gray-700/50"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default AboutUs; 